import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgEye, CgPen, CgTrash } from "react-icons/cg";
import { Modal } from "react-bootstrap";
import CustomModal from "../Commaon/CustomModal";
import ImageWithLoader from "../Commaon/ImageWithLoader";
import { getWebpFileError } from "../../utils/fileValidation";
import {
  getRouteKeyFromLink,
  getRouteLinkLabel,
  getRouteLinkPayload,
  getRouteLinkValue,
  isContactRoute,
  routeOptions,
} from "../../utils/routeOptions";

const BannersList = () => {
  const [banners, setBanners] = useState([]);
  const [filteredBanners, setFilteredBanners] = useState([]);
  const [addModel, setAddModel] = useState(false);
  const [editModel, setEditModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editLink, setEditLink] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState("");
  const [position, setPosition] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [editId, setEditId] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [link, setLink] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [editContactNumber, setEditContactNumber] = useState("");
  const [image, setImage] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [editIsActive, setEditIsActive] = useState(true);
  const [errors, setErrors] = useState({});
  const [viewModel, setViewModel] = useState(false);
  const [viewId, setViewId] = useState(null);

  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const isWebLink = (value) =>
    typeof value === "string" && /^(https?:\/\/|\/)/i.test(value);

  const getBanners = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/banner`, {
        method: "GET",
      });
      const data = response.data;
      if (data.s === 200) {
        setBanners(data.r || []);
        setFilteredBanners(data.r || []);
      }
    } catch (err) {
      console.error("Failed to fetch Banners:", err);
      setSnack({
        notification: { message: "Error fetching banners", type: "error" },
      });
      setBanners([]);
      setFilteredBanners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBanners();
  }, []);

  // search filter
  useEffect(() => {
    if (!query.trim()) {
      setFilteredBanners(banners);
    } else {
      setFilteredBanners(
        banners.filter((c) =>
          c.title?.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
    setPageNo(1);
  }, [query, banners]);

  // pagination slice
  const startIndex = (pageNo - 1) * limit;
  const paginatedBanners = filteredBanners.slice(
    startIndex,
    startIndex + limit
  );

  // action handlers
  const editBanner = (id) => {
    const Banner = banners.find((c) => c._id === id);
    if (Banner) {
      setEditId(id);
      setTitle(Banner.title);
      setEditTitle(Banner.title);
      setEditDescription(Banner.description);
      setEditLink(getRouteKeyFromLink(Banner.link));
      setEditContactNumber(isContactRoute(getRouteKeyFromLink(Banner.link)) ? getRouteLinkValue(Banner.link) : "");
      setEditImage(null);
      setEditImagePreview(Banner.imageUrl || "");
      setEditPosition(Banner.position ?? Banner.order ?? "");
      setEditIsActive(Banner.isActive ?? true);
      setEditModel(true);
    }
  };

  const deleteBanner = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
  };

  const addBanner = () => {
    setIsActive(true);
    setAddModel(true);
  };

  const viewBanner = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/banner/${id}`, {
        method: "GET",
      });
      if (response?.data?.r) {
        setBanners((prev) =>
          prev.map((banner) => (banner._id === id ? { ...banner, ...response.data.r } : banner))
        );
      }
      setViewId(id);
      setViewModel(true);
    } catch (err) {
      setViewId(id);
      setViewModel(true);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Validation for Add/Edit
  const validateAddForm = (isEdit = false) => {
    let newErrors = {};

    if (!isEdit && !title.trim()) {
      newErrors.title = "Banner title is required";
    }
    if (isEdit && !editTitle.trim()) {
      newErrors.title = "Banner title is required";
    }

    if (!isEdit && !description.trim()) {
      newErrors.description = "Description is required";
    }
    if (isEdit && !editDescription.trim()) {
      newErrors.description = "Description is required";
    }

    if (!isEdit && !link.trim()) {
      newErrors.link = "Link is required";
    }
    if (isEdit && !editLink.trim()) {
      newErrors.link = "Link is required";
    }
    const selectedRoute = isEdit ? editLink : link;
    const selectedNumber = isEdit ? editContactNumber : contactNumber;
    if (isContactRoute(selectedRoute) && !/^\d{10}$/.test(selectedNumber)) {
      newErrors.contactNumber = "Enter a valid 10-digit mobile number";
    }

    if (!isEdit && !image) {
      newErrors.image = "Image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddBanner = async (e) => {
    e.preventDefault();
    if (!validateAddForm()) return;

    const formData = new FormData();
    formData.append("title", title);
    formData.append("link", JSON.stringify(getRouteLinkPayload(link, contactNumber)));
    formData.append("description", description);
    const fileError = getWebpFileError(image);
    if (fileError) {
      setErrors((prev) => ({ ...prev, image: fileError }));
      return;
    }
    if (image) formData.append("bannerImage", image);
    if (position !== "") formData.append("position", position);
    formData.append("isActive", String(isActive));

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/banner`, {
        method: "POST",
        body: formData,
      });
      const data = response.data;

      if (data.s === 201) {
        setBanners((prev) => [...prev, data.r]);
        setFilteredBanners((prev) => [...prev, data.r]);
        setSnack({
          notification: {
            message: "Banner added successfully",
            type: "success",
          },
        });
        setAddModel(false);
        setTitle("");
        setLink("");
        setContactNumber("");
        setDescription("");
        setImage(null);
        setPosition("");
        setIsActive(true);
        setErrors({});
      } else {
        throw new Error(data.m || "Failed to add Banner");
      }
    } catch (err) {
      console.error("Error adding Banner:", err);
      setSnack({
        notification: {
          message: err.message || "Error adding Banner",
          type: "error",
        },
      });
    } finally {
      getBanners();
      setLoading(false);
    }
  };

  const handleEditBanner = async (e) => {
    e.preventDefault();
    if (!validateAddForm(true)) return;

    const formData = new FormData();
    formData.append("title", editTitle);
    formData.append("link", JSON.stringify(getRouteLinkPayload(editLink, editContactNumber)));
    formData.append("description", editDescription);
    const fileError = getWebpFileError(editImage);
    if (fileError) {
      setErrors((prev) => ({ ...prev, image: fileError }));
      return;
    }
    if (editImage) formData.append("bannerImage", editImage);
    if (editPosition !== "") formData.append("position", editPosition);
    formData.append("isActive", String(editIsActive));

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/banner/${editId}`, {
        method: "PUT",
        body: formData,
      });
      const data = response.data;

      if (data.s === 200) {
        if (data.r?._id) {
          setBanners((prev) =>
            prev.map((banner) => (banner._id === editId ? data.r : banner))
          );
          setFilteredBanners((prev) =>
            prev.map((banner) => (banner._id === editId ? data.r : banner))
          );
        }
        setSnack({
          notification: {
            message: "Banner updated successfully",
            type: "success",
          },
        });
      } else {
        setSnack({
          notification: {
            message: data.m || "Error updating Banner",
            type: "error",
          },
        });
      }
    } catch (err) {
      console.error("Error updating Banner:", err);
      setSnack({
        notification: {
          message: err.message || "Error updating Banner",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
      setEditModel(false);
      setEditTitle("");
      setEditDescription("");
      setEditId(null);
      setEditImage(null);
      setEditImagePreview("");
      setEditLink("");
      setEditContactNumber("");
      setEditPosition("");
      setEditIsActive(true);
      setTitle("");
      setErrors({});
    }
  };

  const handleDeleteBanner = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/banner/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Banner deleted successfully",
            type: "success",
          },
        });
      }
    } catch (err) {
      console.error("Error deleting Banner:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting Banner",
          type: "error",
        },
      });
    } finally {
      getBanners();
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  const toggleBannerStatus = async (banner) => {
    const nextStatus = !banner.isActive;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/banner/${banner._id}`, {
        method: "PUT",
        body: JSON.stringify({ isActive: nextStatus }),
      });
      const data = response.data;

      if (data.s === 200) {
        setBanners((prev) =>
          prev.map((item) =>
            item._id === banner._id ? { ...item, isActive: nextStatus } : item
          )
        );
        setSnack({
          notification: { message: "Banner status updated", type: "success" },
        });
      } else {
        throw new Error(data.m || "Error updating banner status");
      }
    } catch (err) {
      setSnack({
        notification: { message: err.message || "Error updating banner status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}
      <div className="card shadow-sm border-0 rounded-3 p-3">
        {/* Header + Page Size */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Banners</h2>
          <div>
            <select
              className="form-select form-select-sm "
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <input
              className="form-control"
              style={{ width: "40vw" }}
              placeholder="Search Banners..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="btn btn-outline-custom">Search</button>
          </div>
          <button
            className="btn btn-outline-custom"
            onClick={() => addBanner()}
          >
            Add Banner
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table
            className="table table-hover align-middle"
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <thead className="table-light">
              <tr>
                <th>Sr.</th>
                <th>Banner Title</th>
                <th>Link</th>
                <th>Position</th>
                <th>Image</th>
                <th>Status</th>
                <th style={{ width: "150px" }} className="text-start">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedBanners.length > 0 &&
                paginatedBanners.map((Banner, idx) => (
                  <tr key={Banner._id || idx}>
                    <td>{startIndex + idx + 1}</td>
                    <td>{Banner.title || "-"}</td>
                    <td>
                      {Banner.link ? (
                        isWebLink(getRouteLinkValue(Banner.link)) ? (
                          <a
                            href={getRouteLinkValue(Banner.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none text-truncate d-inline-block"
                            style={{ maxWidth: "220px" }}
                            title={getRouteLinkValue(Banner.link)}
                          >
                            {getRouteLinkLabel(Banner.link)}
                          </a>
                        ) : (
                          <span className="text-dark">{getRouteLinkLabel(Banner.link)}</span>
                        )
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{Banner.position ?? Banner.order ?? "-"}</td>

                    <td>
                      {Banner.imageUrl ? (
                        <ImageWithLoader
                          src={Banner.imageUrl}
                          alt={Banner.title}
                          style={{ width: "40px", height: "40px" }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {Banner.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleBannerStatus(Banner)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleBannerStatus(Banner)}>Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex flex-row justify-content-start gap-2">
                        <button
                          className="btn btn-sm btn-outline-custom "
                          onClick={() => viewBanner(Banner._id)}
                        >
                          <CgEye size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-custom "
                          onClick={() => editBanner(Banner._id)}
                        >
                          <CgPen size={16} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteBanner(Banner._id)}
                        >
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedBanners.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-4">
                    No banners found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {Math.ceil(filteredBanners.length / limit) || 1}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() =>
              setPageNo((prev) =>
                prev < Math.ceil(filteredBanners.length / limit)
                  ? prev + 1
                  : prev
              )
            }
            disabled={pageNo >= Math.ceil(filteredBanners.length / limit)}
          >
            Next
          </button>
        </div>
      </div>
      {/* Add Banner Modal */}
      <Modal show={addModel} onHide={() => setAddModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Banner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddBanner}>
            <div className="mb-3">
              <label className="form-label">Banner Title</label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className={`form-control ${
                  errors.description ? "is-invalid" : ""
                }`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Link</label>
              <select
                className={`form-select ${errors.link ? "is-invalid" : ""}`}
                value={link}
                onChange={(e) => { const value = e.target.value; setLink(value); if (!isContactRoute(value)) setContactNumber(""); }}
                disabled={loading}
              >
                <option value="" disabled>
                  Select redirection route
                </option>
                {routeOptions.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.key}
                  </option>
                ))}
              </select>
              {errors.link && (
                <div className="invalid-feedback">{errors.link}</div>
              )}
            </div>
            {isContactRoute(link) && (
              <div className="mb-3">
                <label className="form-label">Mobile Number</label>
                <input type="tel" inputMode="numeric" maxLength={10} className={`form-control ${errors.contactNumber ? "is-invalid" : ""}`} value={contactNumber} onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} disabled={loading} />
                {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Icon</label>
              <input
                type="file"
                className={`form-control ${errors.image ? "is-invalid" : ""}`}
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setImage(fileError ? null : file);
                  setErrors((prev) => ({ ...prev, image: fileError }));
                }}
                disabled={loading}
              />
              {errors.image && (
                <div className="invalid-feedback">{errors.image}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={isActive ? "true" : "false"}
                onChange={(e) => setIsActive(e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Add Banner"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* Edit Banner Modal */}
      <Modal show={editModel} onHide={() => setEditModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Banner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditBanner}>
            <div className="mb-3">
              <div className="mb-3">Current Title: {title}</div>
              <label className="form-label">Banner Title</label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                disabled={loading}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className={`form-control ${
                  errors.description ? "is-invalid" : ""
                }`}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                disabled={loading}
              />
              {errors.description && (
                <div className="invalid-feedback">{errors.description}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Link</label>
              <select
                className={`form-select ${errors.link ? "is-invalid" : ""}`}
                value={editLink}
                onChange={(e) => { const value = e.target.value; setEditLink(value); if (!isContactRoute(value)) setEditContactNumber(""); }}
                disabled={loading}
              >
                <option value="" disabled>
                  Select redirection route
                </option>
                {routeOptions.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.key}
                  </option>
                ))}
              </select>
              {errors.link && (
                <div className="invalid-feedback">{errors.link}</div>
              )}
            </div>
            {isContactRoute(editLink) && (
              <div className="mb-3">
                <label className="form-label">Mobile Number</label>
                <input type="tel" inputMode="numeric" maxLength={10} className={`form-control ${errors.contactNumber ? "is-invalid" : ""}`} value={editContactNumber} onChange={(e) => setEditContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} disabled={loading} />
                {errors.contactNumber && <div className="invalid-feedback">{errors.contactNumber}</div>}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Icon</label>
              <input
                type="file"
                className="form-control"
                accept="image/webp,image/png,image/jpeg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  const fileError = getWebpFileError(file);
                  setEditImage(fileError ? null : file);
                  setErrors((prev) => ({ ...prev, image: fileError }));
                }}
                disabled={loading}
              />
              {errors.image && (
                <div className="invalid-feedback d-block">{errors.image}</div>
              )}
              {editImagePreview && !editImage && (
                <div className="mt-2">
                  <div className="text-muted small mb-1">Current Image</div>
                  <ImageWithLoader
                    src={editImagePreview}
                    alt={editTitle}
                    style={{ width: "96px", height: "54px", objectFit: "cover" }}
                  />
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                type="number"
                className="form-control"
                value={editPosition}
                onChange={(e) => setEditPosition(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={editIsActive ? "true" : "false"}
                onChange={(e) => setEditIsActive(e.target.value === "true")}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Update Banner"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* View Banner Modal */}
      <Modal
        show={viewModel}
        onHide={() => setViewModel(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="border-0 px-4 pt-4">
          <Modal.Title className="fw-semibold fs-6">
            Banner Information
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 pb-4">
          {viewId ? (
            (() => {
              const banner = banners.find((b) => b._id === viewId);

              return (
                <div className="d-flex flex-column gap-3">
                  {/* Image */}
                  <div className="d-flex justify-content-start">
                    {banner?.imageUrl ? (
                      <div
                        className="border rounded-3 overflow-hidden"
                        style={{ maxWidth: "460px", maxHeight: "220px" }}
                      >
                        <ImageWithLoader
                          src={banner.imageUrl}
                          alt="Banner"
                          className="w-100"
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                    ) : (
                      <span className="text-muted small">
                        No image available
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="border rounded-3 p-3">
                    <div className="mb-2">
                      <div className="text-muted small">Title</div>
                      <div className="fw-medium">{banner?.title || "-"}</div>
                    </div>

                    <div className="mb-2">
                      <div className="text-muted small">Description</div>
                      <div className="text-secondary small">
                        {banner?.description || "-"}
                      </div>
                    </div>

                    <div>
                      <div className="text-muted small">Redirect URL</div>
                      {banner?.link ? (
                        isWebLink(getRouteLinkValue(banner.link)) ? (
                          <a
                            href={getRouteLinkValue(banner.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none small text-break"
                          >
                            {getRouteLinkLabel(banner.link)}
                          </a>
                        ) : (
                          <span className="small text-dark">{getRouteLinkLabel(banner.link)}</span>
                        )
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <p className="text-center text-muted small">
              No banner information available
            </p>
          )}
        </Modal.Body>
      </Modal>
      {/* Delete Confirm */}
      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message="Are you sure you want to delete this Banner?"
        onConfirm={() => {
          handleDeleteBanner(deleteId);
          setDeleteModel(false);
        }}
        onCancel={() => setDeleteModel(false)}
      />
    </>
  );
};

export default BannersList;
